import React, { useState, useEffect } from 'react';
import { Typography, Box, Grid, IconButton, Collapse, Badge, CircularProgress } from '@mui/material';
import { useTheme } from '@mui/system';
import { useTranslation } from 'react-i18next';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

import OperationalNotificationsBusinessLogic from '../../../businessLogic/OperationalNotificationsBusinessLogic';
import { IOperationalNotificationsGuiModel } from '../../../model/IOperationalNotificationsGuiModel';
import { BusinessLogicResult } from '../../../businessLogic/BusinessLogicResult';

const NotificationBoard = (): JSX.Element => {
    const { t } = useTranslation();
    const theme = useTheme();
    const [isOpen, setIsOpen] = useState(false);
    const [notifications, setNotifications] = useState<IOperationalNotificationsGuiModel[]>([]);
    const [loader, setLoader] = useState(false);

    const handleToggle = () => {
        setIsOpen(!isOpen);
    };

    const fetchNotifications = async () => {
        setLoader(true);
        let isMounted = true;

        try {
            const businessLogic = new OperationalNotificationsBusinessLogic();
            const result: BusinessLogicResult<IOperationalNotificationsGuiModel[]> =
                await businessLogic.getOperationalNotifications();

            if (result && result.value) {
                if (isMounted) {
                    setNotifications(result.value);
                }
            } else {
                toast.error(t('home.errorFetchingNotifications'), { position: toast.POSITION.TOP_RIGHT });
            }
        } catch (error) {
            toast.error(t('home.errorFetchingNotifications'), { position: toast.POSITION.TOP_RIGHT });
        } finally {
            if (isMounted) {
                setLoader(false);
            }
        }
        return () => {
            isMounted = false;
        };
    };

    useEffect(() => {
        fetchNotifications();
        const interval = setInterval(() => {
            fetchNotifications();
        }, 30000);
        return () => clearInterval(interval);
    }, []);

    return (
        <Box sx={{ position: 'relative', zIndex: 1 }}>
            {/* Collapsed Header */}
            <Box
                display="flex"
                justifyContent="center"
                alignItems="center"
                sx={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%) translateY(5px)',
                    zIndex: 2,
                    cursor: 'pointer',
                    padding: theme.spacing(1),
                    backgroundColor: theme.palette.grey[200],
                    borderRadius: theme.shape.borderRadius,
                    border: `1px solid ${theme.palette.grey[300]}`,
                    width: 'auto',
                    height: 'auto',
                    gap: theme.spacing(2),
                }}
                onClick={handleToggle}
            >
                <IconButton
                    size="large"
                    sx={{
                        padding: theme.spacing(1),
                        borderRadius: '5%',
                        zIndex: 3,
                        marginRight: theme.spacing(2),
                    }}
                >
                    {isOpen ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                </IconButton>

                <Badge badgeContent={notifications.length} color="primary" sx={{ marginRight: theme.spacing(4) }}></Badge>

                <Typography variant="body1" fontWeight="bold" sx={{ fontSize: '1rem' }}>
                    {t('home.notificationBoard')}
                </Typography>
            </Box>

            {/* Expanded Content */}
            <Collapse
                in={isOpen}
                sx={{
                    position: 'absolute',
                    top: 'calc(100% + 8px)',
                    left: 0,
                    right: 0,
                    backgroundColor: theme.palette.grey[100],
                    zIndex: 1,
                    border: `1px solid ${theme.palette.grey[300]}`,
                    borderRadius: theme.shape.borderRadius,
                    padding: theme.spacing(2),
                    overflow: 'hidden',
                }}
            >
                {loader ? (
                    <Box display="flex" justifyContent="center" alignItems="center">
                        <CircularProgress size={20} />
                    </Box>
                ) : notifications.length > 0 ? (
                    <Box>
                        <Grid container spacing={1}>
                            {/* Header Row */}
                            <Grid item xs={12}>
                                <Box
                                    display="flex"
                                    justifyContent="space-between"
                                    alignItems="center"
                                    sx={{
                                        padding: theme.spacing(1),
                                        backgroundColor: theme.palette.grey[300],
                                        borderRadius: theme.shape.borderRadius,
                                        fontWeight: 'bold',
                                    }}
                                >
                                    <Typography variant="body2" sx={{ flex: 1, textAlign: 'left' }}>
                                        {t('home.source')}
                                    </Typography>
                                    <Typography variant="body2" sx={{ flex: 1, textAlign: 'left' }}>
                                        {t('home.incidentId')}
                                    </Typography>
                                    <Typography variant="body2" sx={{ flex: 1.5, textAlign: 'left' }}>
                                        {t('home.created')}
                                    </Typography>
                                    <Typography variant="body2" sx={{ flex: 1.5, textAlign: 'left' }}>
                                        {t('home.lastUpdated')}
                                    </Typography>
                                    <Typography variant="body2" sx={{ flex: 1.5, textAlign: 'left' }}>
                                        {t('home.resolved')}
                                    </Typography>
                                    <Typography variant="body2" sx={{ flex: 2, textAlign: 'left' }}>
                                        {t('home.message')}
                                    </Typography>
                                </Box>
                            </Grid>

                            {/* Notification Rows */}
                            {notifications.map((notification, index) => (
                                <Grid item xs={12} key={index}>
                                    <Box
                                        display="flex"
                                        justifyContent="space-between"
                                        alignItems="center"
                                        sx={{
                                            padding: theme.spacing(1),
                                            borderBottom: `1px solid ${theme.palette.divider}`,
                                            backgroundColor:
                                                index % 2 === 0
                                                    ? theme.palette.grey[200]
                                                    : theme.palette.grey[100],
                                        }}
                                    >
                                        <Typography variant="body2" sx={{ flex: 1, textAlign: 'left' }}>
                                            {notification.source || 'Unknown'}
                                        </Typography>
                                        <Typography variant="body2" sx={{ flex: 1, textAlign: 'left' }}>
                                            {notification.sourceReference || 'N/A'}
                                        </Typography>
                                        <Typography variant="body2" sx={{ flex: 1.5, textAlign: 'left' }}>
                                            {notification.sourceCreated || 'No date'}
                                        </Typography>
                                        <Typography variant="body2" sx={{ flex: 1.5, textAlign: 'left' }}>
                                            {notification.sourceModified || 'No date'}
                                        </Typography>
                                        <Typography
                                            variant="body2"
                                            sx={{
                                                flex: 1.5,
                                                textAlign: 'left',
                                                color: 'red',
                                                fontWeight: 'bold',
                                            }}
                                        >
                                            {notification.resolved || 'Unresolved'}
                                        </Typography>
                                        <Typography variant="body2" sx={{ flex: 2, textAlign: 'left' }}>
                                            {notification.messageText || 'No message'}
                                        </Typography>
                                    </Box>
                                </Grid>
                            ))}
                        </Grid>
                    </Box>
                ) : (
                    <Typography variant="body2" textAlign="center">
                     {t('home.noNotifications')}
                    </Typography>
                )}
            </Collapse>
        </Box>
    );
};

export default NotificationBoard;
